import { useEditor } from '../../editor-state'
import { Children, cloneElement, Fragment, isValidElement, lazy, Suspense, useState, type ReactNode, type ReactElement } from 'react'
import { blocks, BlockViewer, InlineBlockViewer, type Block } from '@w/content-blocks'
import { CodeHighlightingControls, codeHighlightingSurfaceClassName, type CodeToken } from '@w/code-highlighting'
import { editorCodeRuns } from '../../../../../whiteboard/src/editor-code-runs'
import { Button } from '@w/ui/components/button'
import { Edit2Icon } from '@w/ui/components/icon'
import { IconButton } from '@w/ui/components/icon-button'
import { Input } from '@w/ui/components/input'
import { Popover, PopoverDescription, PopoverPopup, PopoverTitle, PopoverTrigger } from '@w/ui/components/popover'
import { useMediaUrl } from '../../media-url-ctx'
import { usePageInfo } from '../../page-info-ctx'
import { useNestedCodeEditor } from '../../nested-editor-ctx'
import { CodeActionMenu } from '../../code-action-menu'
import { blockEditorTheme } from '../../theme'
import { useTranslation } from '../../i18n'
import { findBlock, plain } from '../../core/tree-read'
import type { BlockView } from '../view-api'
import { useBlockViewServices } from './services'

const MermaidDiagram = lazy(() => import('@w/mermaid/viewer').then(module => ({ default: module.MermaidDiagram })))

export const MediaView: BlockView = ({ block }) => {
  const resolveFileUrl = useMediaUrl()
  const { spaceId, resolvePage } = usePageInfo()
  // A missing display URL also denies disclosure through the editor's HTML copy payload.
  const discloseSource = 'src' in block && !!resolveFileUrl(block.src)
  const media = block.type === 'img' && !block.children?.length && block.alt
    ? { ...block, children: [{ id: `${block.id}:alt`, type: 'text' as const, text: block.alt }] } : block
  return <span data-block-id={block.id} contentEditable={false} data-media-type={block.type}
    data-inline-presentation-type={block.type} data-inline-text-payload={discloseSource ? JSON.stringify({ block }) : undefined}>
    <BlockViewer blocks={[media]} spaceId={spaceId} resolvePage={resolvePage} resolveFileUrl={resolveFileUrl} />
  </span>
}

export const CodeView: BlockView = props => {
  const { t } = useTranslation()
  const localized = { ...props, api: { ...props.api, t } }
  return props.block.type === 'pre' && props.block.latex ? <MathView {...localized} /> : <CodeSource {...localized} />
}

function CodeSource({ block, api, children }: Parameters<BlockView>[0]) {
  const source = plain(block)
  const language = block.type === 'pre' ? block.lang ?? 'text' : 'text'
  if (block.type !== 'pre') return null
  const tokens = editorCodeRuns(source, language)
  return <CodeFrame block={block} api={api} source={source}>{block.children.some(child => child.type === 'diff')
    ? Children.toArray(children).map((child, index) => <Fragment key={isValidElement(child) ? child.key : `text:${typeof child === 'string' || typeof child === 'number' ? child : ''}`}>{index > 0 && '\n\n'}{child}</Fragment>) : highlightChildren(children, tokens)}</CodeFrame>
}

/** Shared source chrome; review supplies highlighted old/new streams inside the same surface. */
export function CodeFrame({ block, api, source, children }: Parameters<BlockView>[0] & { source: string }) {
  const language = block.type === 'pre' ? block.lang ?? 'text' : 'text'
  const { spaceId, resolvePage } = usePageInfo()
  const changeLanguage = (next: string) => { api.dispatch({ type: 'setCodeLanguage', block: block.id, language: next }) }
  const editor = useEditor()
  // Copy reads the code at the click (an IME session finishes on the click's capture, after this render). Review
  // renders a projected source (the proposed side), which stays as rendered.
  const code = source === plain(block) ? () => { const live = findBlock(editor.getSnapshot().blocks, block.id); return live ? plain(live) : source } : source
  return <div className={blockEditorTheme.code} data-code-block={block.id}>
    <div contentEditable={false}>
      {api.layout === 'touch' ? <CodeActionMenu code={code} onLanguageChange={api.mode === 'edit' ? changeLanguage : undefined} />
        : <CodeHighlightingControls code={code} language={language} allowLanguageSelection={api.mode === 'edit'} onLanguageChange={changeLanguage} />}
    </div>
    <pre data-block-id={block.id} data-code-language={language} data-content-blocks-code-source="" className={`${codeHighlightingSurfaceClassName} whitespace-pre-wrap`}>
      <code data-language={language} style={{ display: 'block', whiteSpace: 'inherit', outline: 'none', minHeight: '1.5rem' }}>{children}</code>
    </pre>
    {block.type === 'pre' && block.caption && <figcaption contentEditable={false} data-code-caption="true">
      <BlockViewer blocks={block.caption.flatMap(value => value.type === 'p' ? value.children : [value])}
        className="text-label-medium text-on-surface-secondary mt-2 text-center font-medium" spaceId={spaceId} resolvePage={resolvePage} />
    </figcaption>}
    <div contentEditable={false} data-mermaid-preview="true" data-mermaid-source={language === 'mermaid' ? source : undefined} data-testid="content-blocks-editor-mermaid"
      style={{ display: language === 'mermaid' && source.trim() ? 'block' : 'none', marginTop: '0.75rem', overflowX: 'auto', border: '1px solid var(--color-border)', borderRadius: '0.5rem', background: 'var(--color-surface-0)', padding: '1rem', boxShadow: 'var(--shadow-sm)' }}>
      {language === 'mermaid' && source.trim() && <Suspense><MermaidDiagram code={source} /></Suspense>}
    </div>
  </div>
}

const toneClass = { error: 'text-error', warning: 'text-warning', info: 'text-info', success: 'text-success', primary: 'text-primary', muted: 'text-on-surface-secondary' }

/** Retain the core leaf IDs; token spans never become editing identities. */
function highlightChildren(children: ReactNode, tokens: CodeToken[]): ReactNode {
  let offset = 0
  const spans = tokens.map(token => {
    const start = offset
    offset += token.text.length
    return { ...token, start, end: offset }
  })
  offset = 0
  const walk = (nodes: ReactNode): ReactNode => Children.map(nodes, node => {
    if (typeof node === 'string') {
      const start = offset
      offset += node.length
      return spans.filter(token => token.end > start && token.start < offset).map(token => <span key={`${start}-${token.start}`}
        className={token.tone ? toneClass[token.tone] : undefined}>{node.slice(Math.max(0, token.start - start), Math.min(node.length, token.end - start))}</span>)
    }
    if (!isValidElement(node)) return node
    const element = node as ReactElement<{ children?: ReactNode; 'data-leaf'?: string }>
    if (element.type === 'br') { offset += 1; return '\n' }
    const content = element.props.children
    // Core leaves end a trailing newline with a keyless caret `<br>`; source newlines stay literal text, and the
    // terminating `<br>` stays an element (Lexical managed line break) so the caret after the newline renders.
    const last: unknown = Array.isArray(content) ? content.at(-1) : undefined
    if (element.props['data-leaf'] && Array.isArray(content) && isValidElement(last) && last.type === 'br' && last.key === null) {
      return cloneElement(element, {}, walk(content.slice(0, -1)), <br key="caret-line" />)
    }
    return cloneElement(element, {}, walk(content))
  })
  return walk(children)
}

export const MathView: BlockView = ({ block, api }) => {
  const { updateBlock } = useBlockViewServices()
  const { spaceId, resolvePage } = usePageInfo()
  const renderCodeEditor = useNestedCodeEditor()
  const [open, setOpen] = useState(false)
  if (block.type !== 'code' && block.type !== 'pre') return null
  const inline = block.type === 'code'
  const disclose = api.mode === 'edit' || inline && api.mode !== 'answer'
  const value = blocks.serialize(block.children)
  const commit = (source: string) => {
    const first = block.children[0]
    const children: Block[] = [{ id: first?.id ?? '', type: 'text', text: source }]
    updateBlock(block.id, { children })
  }
  const Viewer = inline ? InlineBlockViewer : BlockViewer
  const Tag = inline ? 'span' : 'div'
  return <Tag data-block-id={block.id} contentEditable={false} data-inline-presentation-type={inline ? 'code' : undefined}
    data-presentation-type={inline ? undefined : 'pre'} className={inline ? 'group/inline-presentation relative' : 'group/presentation relative'}>
    {(!inline || !disclose) && <Viewer blocks={[block]} spaceId={spaceId} resolvePage={resolvePage} />}
    {disclose && <Popover open={open} onOpenChange={setOpen}>
      {inline ? <PopoverTrigger render={<Button aria-label="Edit formula" variant="text"
        className="mx-0.5 inline-flex h-auto min-h-0 px-0.5 py-0 align-baseline font-normal" data-testid={`inline-presentation-edit-${block.id}`} />}>
        <span className="sr-only">{`Edit formula ${value}`}</span><InlineBlockViewer blocks={[block]} spaceId={spaceId} resolvePage={resolvePage} />
      </PopoverTrigger> : <PopoverTrigger render={<IconButton size="sm" variant="outlined"
        className={`border-border bg-surface-1 text-on-surface-secondary absolute top-6 right-2 z-20 shadow-sm ${api.layout === 'touch' ? '' : 'opacity-0 group-hover/presentation:opacity-100 focus-visible:opacity-100'}`}
        data-testid={`presentation-edit-${block.id}`} />}><Edit2Icon />Edit formula</PopoverTrigger>}
      <PopoverPopup align={inline ? 'start' : 'end'} className={inline ? 'w-80' : 'w-96'}><div className="grid gap-4">
        <div className="grid gap-1"><PopoverTitle>Formula</PopoverTitle><PopoverDescription>Edit LaTeX source.</PopoverDescription></div>
        {renderCodeEditor ? <div data-testid={`${inline ? 'inline-' : ''}presentation-source-${block.id}`}>{renderCodeEditor({ language: 'latex', value, onChange: commit })}</div>
          : <Input aria-label="Math source" data-testid={`${inline ? 'inline-' : ''}presentation-source-${block.id}`} value={value}
            onInput={event => { commit(event.currentTarget.value) }} />}
        {!inline && <Button data-testid={`presentation-done-${block.id}`} onClick={() => { setOpen(false) }} variant="filled" size="sm">{api.t('contentBlocksEditor.document.done')}</Button>}
      </div></PopoverPopup>
    </Popover>}
  </Tag>
}
